import {
  useGetUserPreferencesQuery,
  useUpdateUserStructurePreferencesMutation,
  GetUserPreferencesDocument,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { extractErrorMessageFromUnknownError } from '@/utils/error';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useCallback, useMemo } from 'react';

type UsePersistFiltersOptions = {
  filterKey: string;
};

export const usePersistFilters = ({ filterKey }: UsePersistFiltersOptions): {
  savedFilterKeys: string[] | null | undefined;
  persistFilterKeys: (filterKeys: string[]) => Promise<void>;
  loading: boolean;
  isInitialized: boolean;
} => {
  const structureId = useGetStructureId();
  const { showSnackBar } = useSnackBarMessage({});

  // network-only to avoid stale cached data when switching contexts
  const { data: userPreferencesData, loading: preferencesLoading } = useGetUserPreferencesQuery({
    fetchPolicy: 'network-only',
  });

  const [mutate, { loading: mutationLoading }] = useUpdateUserStructurePreferencesMutation();

  const { savedFilterKeys, isInitialized } = useMemo(() => {
    // Still loading or missing required params
    if (preferencesLoading || !structureId || !filterKey) {
      return { savedFilterKeys: null, isInitialized: false };
    }

    // Extract saved filter keys from user preferences
    const structure = userPreferencesData?.getUserPreferences?.structure;
    const structurePrefs = structure?.[structureId];
    const filters = structurePrefs?.filters?.[filterKey] as string[] | undefined;

    return { savedFilterKeys: filters, isInitialized: true };
  }, [structureId, userPreferencesData, preferencesLoading, filterKey]);

  const persistFilterKeys = useCallback(
    async (filterKeys: string[]) => {
      if (!structureId || !filterKey) {
        return;
      }

      try {
        await mutate({
          variables: {
            input: {
              structureId,
              set: {
                filters: {
                  [filterKey]: filterKeys,
                },
              },
            },
          },
          refetchQueries: [GetUserPreferencesDocument],
        });
      } catch (error) {
        const message = extractErrorMessageFromUnknownError(error);
        showSnackBar(message, 'error');
      }
    },
    [mutate, structureId, filterKey, showSnackBar]
  );

  return {
    savedFilterKeys,
    persistFilterKeys,
    loading: preferencesLoading || mutationLoading,
    isInitialized,
  };
};
